// src/components/LineSectionCard/index.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { URL } from "../../utils/constants";
import { getUser } from "../../utils/api";
import { toast } from "../map/toast";
import styles from "./styles.module.css";
import { FaRegCopy, FaPlus, FaTrash, FaChevronDown } from "react-icons/fa";

const LineSectionCard = () => {
  const { name } = useParams(); // id перегона

  const [details, setDetails] = useState({});
  const [canArchive, setCanArchive] = useState(false);

  const currentArchives = details.archive || [];

  // серверные списки для выбора
  const [listInternal, setListInternal] = useState([]);
  const [listExternal, setListExternal] = useState([]);

  // выбранные элементы (мультивыбор)
  const [selInt, setSelInt] = useState(new Set());
  const [selExt, setSelExt] = useState(new Set());

  // состояния загрузки/ошибок
  const [loadInt, setLoadInt] = useState(false);
  const [loadExt, setLoadExt] = useState(false);
  const [errInt, setErrInt] = useState("");
  const [errExt, setErrExt] = useState("");

  // строки поиска
  const [qInternal, setQInternal] = useState("");
  const [qExternal, setQExternal] = useState("");

  // форма «добавить новый»
  const [form, setForm] = useState({ representation_name: "", archive_link: "" });

  // сворачиваемые блоки (по умолчанию закрыты)
  const [openAttach, setOpenAttach] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);

  const token = localStorage.getItem("auth_token");
  const authHeader = token ? { authorization: `Token ${token}` } : {};

  // загрузка перегона
  useEffect(() => {
    fetch(`${URL}/api/linesections/${name}/`, { method: "GET", headers: authHeader })
      .then((r) => r.json())
      .then((data) => setDetails(data))
      .catch((e) => console.error("GET linesection error:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, token]);

  // права
  useEffect(() => {
    if (!token) return;
    getUser()
      .then((me) => {
        const ok = (me?.groups || []).includes("archive") || !!me?.is_staff;
        setCanArchive(ok);
      })
      .catch((e) => console.error("GET me error:", e));
  }, [token]);

  // helper: копирование
  const copyTextToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
      toast.success("Ссылка скопирована");
    } catch (err) {
      console.error("Ошибка копирования:", err);
      toast.error("Не удалось скопировать ссылку");
    }
  };

  // helper: нормализованный href
  const toHref = (raw = "") => {
    const link = String(raw || "").trim();
    if (!link) return "#";
    if (/^https?:\/\//i.test(link)) return link;
    if (/^file:\/\//i.test(link)) return link;
    if (/^[a-zA-Z]:\\/.test(link)) return "file:///" + link.replace(/\\/g, "/");
    if (/^\\\\/.test(link)) {
      const noLeading = link.replace(/^\\\\/, "");
      return "file://" + noLeading.replace(/\\/g, "/");
    }
    return link;
  };

  // key для элемента архива
  const keyOf = (a) => String(a?.id ?? a?.archive_link ?? "");

  // открепить (как раньше — удаляет сам объект архива)
  const detachArchive = async (archiveId) => {
    if (!token) {
      toast.error("Нужна авторизация.");
      return;
    }

    const confirmed = await toast.confirm("Открепить архив?");

    if (!confirmed) return;

    const toastId = toast.loading("Открепляем архив...");

    try {
      const resp = await fetch(`${URL}/api/archives/${archiveId}/`, {
        method: "DELETE",
        headers: { ...authHeader },
      });

      if (resp.status !== 204) {
        const t = await resp.text();
        throw new Error(t || "Ошибка удаления");
      }

      setDetails((prev) => ({
        ...prev,
        archive: (prev.archive || []).filter((a) => a.id !== archiveId),
      }));

      toast.update(toastId, "Архив откреплён", "success");
    } catch (e) {
      console.error(e);
      toast.update(toastId, "Не удалось открепить архив", "error");
    }
  };

  // создать новый архив и сразу прикрепить к текущему перегону
  const submitNew = async (e) => {
    e?.preventDefault?.();

    if (!token) {
      toast.error("Нужна авторизация.");
      return;
    }

    const nm = form.representation_name.trim();
    const lk = form.archive_link.trim();

    if (!nm || !lk) {
      toast.warn("Укажите и название, и ссылку.");
      return;
    }

    const payload = {
      representation_name: nm,
      archive_link: lk,
      target_model: "linesections",
      target_id: Number(name),
    };

    const toastId = toast.loading("Добавляем архив...");

    try {
      const resp = await fetch(`${URL}/api/archives/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Ошибка создания");
      }

      const created = await resp.json();

      setDetails((prev) => ({
        ...prev,
        archive: [created, ...(prev.archive || [])],
      }));

      setForm({ representation_name: "", archive_link: "" });
      setOpenAdd(false);

      toast.update(toastId, "Архив добавлен", "success");
    } catch (err) {
      console.error(err);
      toast.update(toastId, "Не удалось добавить архив", "error");
    }
  };

  // прикрепить уже существующий — группой
  const attachMany = async (items) => {
    if (!token) {
      toast.error("Нужна авторизация.");
      return false;
    }

    if (!items?.length) {
      toast.warn("Выберите элементы для прикрепления.");
      return false;
    }

    const toastId = toast.loading("Прикрепляем архивы...");

    try {
      const createdList = await Promise.all(
        items.map((src) =>
          fetch(`${URL}/api/archives/`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader },
            body: JSON.stringify({
              representation_name: src.representation_name,
              archive_link: src.archive_link,
              target_model: "linesections",
              target_id: Number(name),
            }),
          }).then(async (r) => {
            if (!r.ok) throw new Error(await r.text());
            return r.json();
          })
        )
      );

      setDetails((prev) => ({
        ...prev,
        archive: [...createdList, ...(prev.archive || [])],
      }));

      toast.update(
        toastId,
        createdList.length === 1 ? "Архив прикреплён" : `Архивы прикреплены: ${createdList.length}`,
        "success"
      );

      return true;
    } catch (e) {
      console.error(e);
      toast.update(toastId, "Часть архивов прикрепить не удалось", "error");
      return false;
    }
  };

  const attachSelected = async (kind) => {
    const list = kind === "internal" ? listInternal : listExternal;
    const sel = kind === "internal" ? selInt : selExt;
    const items = list.filter((a) => sel.has(keyOf(a)));

    const ok = await attachMany(items);

    if (!ok) return;

    if (kind === "internal") {
      setSelInt(new Set());
    } else {
      setSelExt(new Set());
    }
  };

  // классификация уже прикреплённых ссылок
  const groups = useMemo(() => {
    const g = { internal: [], external: [], others: [] };
    for (const el of currentArchives) {
      const link = el?.archive_link || "";
      if (/^T:/i.test(link)) g.internal.push(el);
      else if (/^P:/i.test(link)) g.external.push(el);
      else g.others.push(el);
    }
    return g;
  }, [currentArchives]);

  // серверные загрузчики списков с дебаунсом
  useEffect(() => {
    if (!canArchive) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setLoadInt(true);
      setErrInt("");
      const q = qInternal ? `&q=${encodeURIComponent(qInternal)}` : "";
      fetch(`${URL}/api/archives/?kind=internal${q}`, {
        headers: authHeader,
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
          setListInternal(arr);
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            setErrInt("Не удалось загрузить внутренние ссылки");
            toast.error("Не удалось загрузить внутренние ссылки");
          }
          console.error(e);
        })
        .finally(() => setLoadInt(false));
    }, 250);
    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canArchive, token, qInternal]);

  useEffect(() => {
    if (!canArchive) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setLoadExt(true);
      setErrExt("");
      const q = qExternal ? `&q=${encodeURIComponent(qExternal)}` : "";
      fetch(`${URL}/api/archives/?kind=external${q}`, {
        headers: authHeader,
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
          setListExternal(arr);
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            setErrExt("Не удалось загрузить внешние ссылки");
            toast.error("Не удалось загрузить внешние ссылки");
          }
          console.error(e);
        })
        .finally(() => setLoadExt(false));
    }, 250);
    return () => {
      ctrl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canArchive, token, qExternal]);

  // рендер уже прикреплённых ссылок
  const renderLinks = (links) =>
    links.map((element) => {
      const href = toHref(element.archive_link);
      return (
        <div key={`${element.id}-${element.archive_link}`} className={styles.linkRow}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            title={element.archive_link}
          >
            {element.representation_name}
          </a>
          <button
            type="button"
            className={styles.iconBtn}
            title="Копировать ссылку"
            onClick={() => copyTextToClipboard(element.archive_link)}
          >
            <FaRegCopy />
          </button>
          {canArchive && (
            <button
              type="button"
              className={styles.iconBtnDanger}
              title="Открепить"
              onClick={() => detachArchive(element.id)}
            >
              <FaTrash />
            </button>
          )}
        </div>
      );
    });

  // переключатель выбора в списке
  const toggleSelect = (kind, item) => {
    const k = keyOf(item);
    if (!k) return;
    if (kind === "internal") {
      const next = new Set(selInt);
      next.has(k) ? next.delete(k) : next.add(k);
      setSelInt(next);
    } else {
      const next = new Set(selExt);
      next.has(k) ? next.delete(k) : next.add(k);
      setSelExt(next);
    }
  };

  // строка списков (общий рендер)
  const renderPickList = (kind, list, loading, error, q, setQ, selectedSet) => (
    <div className={styles.attachRow}>
      <div className={styles.label}>{kind === "internal" ? "Внутренняя ссылка" : "Внешняя ссылка"}</div>
      <input
        className={styles.searchInput}
        placeholder="Поиск по названию…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className={styles.scrollList} role="listbox" aria-multiselectable="true" tabIndex={0}>
        {loading ? (
          <li className={styles.muted}>Загрузка…</li>
        ) : error ? (
          <li className={styles.error}>{error}</li>
        ) : list.length ? (
          list.map((a) => {
            const id = keyOf(a);
            const selected = selectedSet.has(id);
            return (
              <li
                key={`${kind}-${id}`}
                className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                onClick={() => toggleSelect(kind, a)}
                aria-selected={selected}
              >
                <span className={styles.listItemText} title={a.representation_name}>
                  {a.representation_name || "(без названия)"}
                </span>
                <span className={styles.checkMark} aria-hidden="true">
                  {selected ? "✓" : ""}
                </span>
              </li>
            );
          })
        ) : (
          <li className={styles.empty}>Ничего не найдено</li>
        )}
      </ul>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => attachSelected(kind)}
        disabled={!selectedSet.size}
        title={!selectedSet.size ? "Выберите элементы" : "Прикрепить выбранные"}
      >
        Прикрепить выбранные ({selectedSet.size})
      </button>
    </div>
  );

  return (
    <div className={styles.cardPage}>
      <div className={styles.card}>
        <div className={styles.cardScroll}>
          <div className={styles.titleRow}>
            <div className={styles.title}>{details.name || "Перегон"}</div>
          </div>
  
          {groups.internal.length > 0 && (
            <div className={styles.panel}>
              <h3 className={styles.h3}>Внутренние ссылки</h3>
              {renderLinks(groups.internal)}
            </div>
          )}
  
          {groups.external.length > 0 && (
            <div className={styles.panel}>
              <h3 className={styles.h3}>Внешние ссылки</h3>
              {renderLinks(groups.external)}
            </div>
          )}
  
          {groups.others.length > 0 && (
            <div className={styles.panel}>
              <h3 className={styles.h3}>Другие ссылки</h3>
              {renderLinks(groups.others)}
            </div>
          )}
  
          {!groups.internal.length && !groups.external.length && !groups.others.length && (
            <div className={styles.panel}>
              <div className={styles.empty}>Архивные ссылки не добавлены</div>
            </div>
          )}
  
          {canArchive && (
            <div className={styles.archivePanel}>
              {/* ─── СВЕРНУТО: Прикрепить из существующих ─── */}
              <button
                type="button"
                className={styles.collapseHeader}
                onClick={() => setOpenAttach((v) => !v)}
                aria-expanded={openAttach}
              >
                <span className={styles.collapseTitle}>Прикрепить из существующих</span>
                <FaChevronDown className={`${styles.chevron} ${openAttach ? styles.chevronOpen : ""}`} />
              </button>
  
              {openAttach && (
                <div className={styles.collapseBody}>
                  {renderPickList("internal", listInternal, loadInt, errInt, qInternal, setQInternal, selInt)}
                  {renderPickList("external", listExternal, loadExt, errExt, qExternal, setQExternal, selExt)}
                </div>
              )}
  
              {/* ─── СВЕРНУТО: Добавить новый архив ─── */}
              <button
                type="button"
                className={styles.collapseHeader}
                onClick={() => setOpenAdd((v) => !v)}
                aria-expanded={openAdd}
              >
                <span className={styles.collapseTitle}>
                  <FaPlus style={{ marginRight: 6 }} />
                  Добавить новый архив
                </span>
                <FaChevronDown className={`${styles.chevron} ${openAdd ? styles.chevronOpen : ""}`} />
              </button>
  
              {openAdd && (
                <div className={styles.collapseBody}>
                  <form onSubmit={submitNew} className={styles.formGrid}>
                    <label className={styles.label}>
                      Название
                      <input
                        type="text"
                        className={styles.input}
                        value={form.representation_name}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, representation_name: e.target.value }))
                        }
                        placeholder="Напр. «Том 4 — ПД»"
                      />
                    </label>
  
                    <label className={styles.label}>
                      Ссылка
                      <input
                        type="text"
                        className={styles.input}
                        value={form.archive_link}
                        onChange={(e) => setForm((f) => ({ ...f, archive_link: e.target.value }))}
                        placeholder="T:\\... или https://..."
                      />
                    </label>
  
                    <button type="submit" className={styles.primaryBtnWide}>
                      Добавить
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LineSectionCard;
